import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, IconButton,
  Tooltip, Grid, Divider, Switch, FormControlLabel, Button,
} from '@mui/material';
import {
  Close, ZoomIn, ZoomOut, Refresh, Speed,
  Public, Info, CenterFocusStrong, Fullscreen, FullscreenExit,
} from '@mui/icons-material';

// ─── Planet Data ──────────────────────────────────────────────────────────────
const PLANET_DATA = [
  {
    name: 'Mercury', type: 'Terrestrial', color: 0xb5b5b5,
    radius: 0.4, orbitRadius: 8,  speed: 4.74,  tilt: 0.03,
    distance: '57.9M km', period: '88 days',    moons: 0,
    mass: '3.3 × 10²³ kg', temp: '167°C avg',  speedKms: 47.9,
    description: 'Smallest planet. Closest to the Sun. No atmosphere.',
    emissive: 0x222222,
  },
  {
    name: 'Venus', type: 'Terrestrial', color: 0xe8cda0,
    radius: 0.9, orbitRadius: 13, speed: 3.50,  tilt: 177.4,
    distance: '108.2M km', period: '225 days',  moons: 0,
    mass: '4.87 × 10²⁴ kg', temp: '464°C avg', speedKms: 35.0,
    description: 'Hottest planet. Thick CO₂ atmosphere causes extreme greenhouse effect.',
    emissive: 0x332200,
  },
  {
    name: 'Earth', type: 'Terrestrial', color: 0x4fc3f7,
    radius: 1.0, orbitRadius: 18, speed: 2.98,  tilt: 23.4,
    distance: '149.6M km', period: '365 days',  moons: 1,
    mass: '5.97 × 10²⁴ kg', temp: '15°C avg',  speedKms: 29.8,
    description: 'Our home. Only known planet with life and liquid water.',
    emissive: 0x001133,
    hasRing: false,
  },
  {
    name: 'Mars', type: 'Terrestrial', color: 0xef5350,
    radius: 0.55, orbitRadius: 25, speed: 2.41, tilt: 25.2,
    distance: '227.9M km', period: '687 days',  moons: 2,
    mass: '6.39 × 10²³ kg', temp: '-60°C avg', speedKms: 24.1,
    description: 'The Red Planet. Has the largest volcano — Olympus Mons.',
    emissive: 0x220000,
  },
  {
    name: 'Jupiter', type: 'Gas Giant', color: 0xc9a96e,
    radius: 3.5, orbitRadius: 40, speed: 1.31,  tilt: 3.1,
    distance: '778.5M km', period: '12 years',  moons: 95,
    mass: '1.90 × 10²⁷ kg', temp: '-110°C avg', speedKms: 13.1,
    description: 'Largest planet. Great Red Spot is a storm larger than Earth.',
    emissive: 0x221100,
    hasStripes: true,
  },
  {
    name: 'Saturn', type: 'Gas Giant', color: 0xe4d191,
    radius: 2.8, orbitRadius: 55, speed: 0.97,  tilt: 26.7,
    distance: '1.43B km', period: '29 years',   moons: 146,
    mass: '5.68 × 10²⁶ kg', temp: '-140°C avg', speedKms: 9.7,
    description: 'Famous ring system made of ice and rock particles.',
    emissive: 0x221100,
    hasRings: true,
  },
  {
    name: 'Uranus', type: 'Ice Giant', color: 0x7de8e8,
    radius: 1.8, orbitRadius: 68, speed: 0.68,  tilt: 97.8,
    distance: '2.87B km', period: '84 years',   moons: 28,
    mass: '8.68 × 10²⁵ kg', temp: '-195°C avg', speedKms: 6.8,
    description: 'Ice giant that rotates on its side. Has faint rings.',
    emissive: 0x001122,
    hasRings: true,
    ringColor: 0x88cccc,
    ringOpacity: 0.3,
  },
  {
    name: 'Neptune', type: 'Ice Giant', color: 0x4b70dd,
    radius: 1.7, orbitRadius: 80, speed: 0.54,  tilt: 28.3,
    distance: '4.50B km', period: '165 years',  moons: 16,
    mass: '1.02 × 10²⁶ kg', temp: '-200°C avg', speedKms: 5.4,
    description: 'Windiest planet. Speeds up to 2,100 km/h.',
    emissive: 0x000022,
  },
];

// Real J2000 orbital elements: {a(AU), T(days), e, inc(°), peri(°), L0(°)}
const ORBITAL_ELEMENTS_JS = {
  Mercury: { a: 0.387098, T: 87.969,   e: 0.20563, inc: 7.005,  peri: 77.456,  L0: 252.251 },
  Venus:   { a: 0.723332, T: 224.701,  e: 0.00677, inc: 3.395,  peri: 131.532, L0: 181.979 },
  Earth:   { a: 1.000000, T: 365.256,  e: 0.01671, inc: 0.000,  peri: 102.937, L0: 100.464 },
  Mars:    { a: 1.523679, T: 686.971,  e: 0.09341, inc: 1.850,  peri: 336.041, L0: 355.453 },
  Jupiter: { a: 5.202887, T: 4332.589, e: 0.04839, inc: 1.303,  peri: 14.728,  L0: 34.396  },
  Saturn:  { a: 9.536676, T: 10759.22, e: 0.05386, inc: 2.489,  peri: 92.432,  L0: 49.955  },
  Uranus:  { a: 19.18916, T: 30688.5,  e: 0.04726, inc: 0.773,  peri: 170.964, L0: 313.232 },
  Neptune: { a: 30.06992, T: 60182.0,  e: 0.00859, inc: 1.770,  peri: 44.971,  L0: 304.880 },
};

/** Returns real heliocentric ecliptic coordinates in AU using Kepler's equation */
function computeKeplerPosition(planetName) {
  const elem = ORBITAL_ELEMENTS_JS[planetName];
  if (!elem) return { x_au: 0, y_au: 0, angle_deg: 0 };
  const { a, T, e, inc, peri, L0 } = elem;

  const daysSinceJ2000 = Date.now() / 86400000 - 10957.5;
  const n = 360.0 / T;
  let M = ((L0 - peri) + n * daysSinceJ2000) % 360.0;
  if (M < 0) M += 360.0;
  const Mrad = M * Math.PI / 180;

  // Solve Kepler's equation (Newton–Raphson, 10 iterations)
  let E = Mrad;
  for (let i = 0; i < 10; i++) {
    E = E - (E - e * Math.sin(E) - Mrad) / (1 - e * Math.cos(E));
  }

  const sinV = Math.sqrt(1 - e * e) * Math.sin(E) / (1 - e * Math.cos(E));
  const cosV = (Math.cos(E) - e) / (1 - e * Math.cos(E));
  const vDeg = Math.atan2(sinV, cosV) * 180 / Math.PI;

  let lon = (vDeg + peri) % 360.0;
  if (lon < 0) lon += 360.0;

  const r = a * (1 - e * e) / (1 + e * Math.cos(vDeg * Math.PI / 180));
  const lonRad = lon * Math.PI / 180;
  const incRad = inc * Math.PI / 180;

  return {
    x_au:      Math.round(r * Math.cos(lonRad) * 10000) / 10000,
    y_au:      Math.round(r * Math.sin(lonRad) * Math.cos(incRad) * 10000) / 10000,
    angle_deg: Math.round(lon * 100) / 100,
  };
}

const PLANET_COLORS_HEX = {
  Mercury: '#b5b5b5', Venus: '#e8cda0', Earth: '#ffffff',
  Mars: '#ef5350', Jupiter: '#c9a96e', Saturn: '#e4d191',
  Uranus: '#7de8e8', Neptune: '#4b70dd', Sun: '#ffd600',
};

export default function SolarSystem3D() {
  const mountRef    = useRef(null);
  const sceneRef    = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef   = useRef(null);
  const animRef     = useRef(null);
  const planetsRef  = useRef([]);
  const orbitsRef   = useRef([]);
  const trailsRef   = useRef([]);
  const clockRef    = useRef(0);
  const mouseRef    = useRef({ x: 0, y: 0 });
  const isDragging  = useRef(false);
  const lastMouse   = useRef({ x: 0, y: 0 });
  const camTheta    = useRef(0.3);
  const camPhi      = useRef(1.1);
  const camRadius   = useRef(90);

  const [selected,    setSelected]    = useState(null);
  const [showOrbits,  setShowOrbits]  = useState(true);
  const [showTrails,  setShowTrails]  = useState(true);
  const [showLabels,  setShowLabels]  = useState(true);
  const [simSpeed,    setSimSpeed]    = useState(1);
  const [threeLoaded, setThreeLoaded] = useState(false);
  const [hoveredName,  setHoveredName]  = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);
  const labelsRef   = useRef([]);

  // ── Load Three.js ────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.THREE) { setThreeLoaded(true); return; }
    const script   = document.createElement('script');
    script.src     = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
    script.onload  = () => setThreeLoaded(true);
    script.onerror = () => console.error('Three.js failed to load');
    document.head.appendChild(script);
  }, []);

  // ── Build scene ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!threeLoaded || !mountRef.current) return;
    const THREE = window.THREE;
    const W     = mountRef.current.clientWidth;
    const H     = mountRef.current.clientHeight;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020408);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(60, W / H, 0.1, 2000);
    updateCameraPos(camera);
    cameraRef.current = camera;

    // Stars background
    const starGeo  = new THREE.BufferGeometry();
    const starCount = 3000;
    const starPos  = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount * 3; i++) {
      starPos[i] = (Math.random() - 0.5) * 1800;
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
    const starMat  = new THREE.PointsMaterial({ color: 0xffffff, size: 0.4, sizeAttenuation: true });
    scene.add(new THREE.Points(starGeo, starMat));

    // Ambient light
    scene.add(new THREE.AmbientLight(0x111122, 0.4));

    // Sun
    const sunGeo  = new THREE.SphereGeometry(5, 32, 32);
    const sunMat  = new THREE.MeshBasicMaterial({ color: 0xffd600 });
    const sun     = new THREE.Mesh(sunGeo, sunMat);
    sun.userData  = { name: 'Sun', isSun: true };
    scene.add(sun);

    // Sun glow layers
    for (let i = 1; i <= 3; i++) {
      const glowGeo = new THREE.SphereGeometry(5 + i * 1.2, 32, 32);
      const glowMat = new THREE.MeshBasicMaterial({
        color: i === 1 ? 0xffaa00 : i === 2 ? 0xff6600 : 0xff3300,
        transparent: true, opacity: 0.06 / i,
        side: THREE.BackSide,
      });
      scene.add(new THREE.Mesh(glowGeo, glowMat));
    }

    // Sun light
    const sunLight = new THREE.PointLight(0xffffff, 2.5, 500);
    sunLight.position.set(0, 0, 0);
    scene.add(sunLight);

    // Asteroid belt
    const beltGeo = new THREE.BufferGeometry();
    const beltPos = new Float32Array(800 * 3);
    for (let i = 0; i < 800; i++) {
      const angle  = Math.random() * Math.PI * 2;
      const r      = 30 + Math.random() * 5;
      const y      = (Math.random() - 0.5) * 1.5;
      beltPos[i*3]   = Math.cos(angle) * r;
      beltPos[i*3+1] = y;
      beltPos[i*3+2] = Math.sin(angle) * r;
    }
    beltGeo.setAttribute('position', new THREE.BufferAttribute(beltPos, 3));
    const beltMat = new THREE.PointsMaterial({ color: 0x888888, size: 0.25, sizeAttenuation: true });
    scene.add(new THREE.Points(beltGeo, beltMat));

    // Planets
    const planetMeshes = [];
    PLANET_DATA.forEach((p, idx) => {
      // Orbit ring
      const orbitGeo = new THREE.RingGeometry(p.orbitRadius - 0.05, p.orbitRadius + 0.05, 128);
      const orbitMat = new THREE.MeshBasicMaterial({
        color: 0x4fc3f7, transparent: true, opacity: 0.12, side: THREE.DoubleSide,
      });
      const orbitRing = new THREE.Mesh(orbitGeo, orbitMat);
      orbitRing.rotation.x = Math.PI / 2;
      scene.add(orbitRing);
      orbitsRef.current.push(orbitRing);

      // Planet sphere
      const geo = new THREE.SphereGeometry(p.radius, 32, 32);
      const mat = new THREE.MeshPhongMaterial({
        color:    p.color,
        emissive: p.emissive || 0x000000,
        emissiveIntensity: 0.1,
        shininess: 30,
      });

      // Jupiter stripes
      if (p.hasStripes) {
        mat.color = new THREE.Color(0xc9a96e);
      }

      const mesh       = new THREE.Mesh(geo, mat);
      mesh.castShadow  = true;
      mesh.userData    = { ...p, idx, isPlanet: true };

      // Pivot for orbit
      const pivot      = new THREE.Object3D();
      pivot.add(mesh);
      scene.add(pivot);

      // Initial position
      const startAngle = (idx / PLANET_DATA.length) * Math.PI * 2;
      mesh.position.x  = p.orbitRadius;
      pivot.rotation.y = startAngle;

      // Saturn rings
      if (p.hasRings && p.name === 'Saturn') {
        const ringGeo = new THREE.RingGeometry(p.radius * 1.4, p.radius * 2.4, 64);
        const ringMat = new THREE.MeshBasicMaterial({
          color: 0xc8b89a, transparent: true, opacity: 0.65,
          side: THREE.DoubleSide,
        });
        const ring    = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.x = Math.PI / 2.5;
        mesh.add(ring);
      }
      // Uranus rings
      if (p.hasRings && p.name === 'Uranus') {
        const ringGeo = new THREE.RingGeometry(p.radius * 1.3, p.radius * 1.9, 64);
        const ringMat = new THREE.MeshBasicMaterial({
          color: 0x88cccc, transparent: true, opacity: 0.3,
          side: THREE.DoubleSide,
        });
        const ring    = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.x = Math.PI / 2;
        mesh.add(ring);
      }

      // Tilt
      mesh.rotation.z = THREE.MathUtils.degToRad(p.tilt);

      // Trail
      const trailGeo  = new THREE.BufferGeometry();
      const trailPos  = new Float32Array(120 * 3);
      trailGeo.setAttribute('position', new THREE.BufferAttribute(trailPos, 3));
      const trailMat  = new THREE.LineBasicMaterial({
        color: p.color, transparent: true, opacity: 0.35,
      });
      const trail     = new THREE.Line(trailGeo, trailMat);
      scene.add(trail);
      trailsRef.current.push({ line: trail, positions: [], maxLen: 120 });

      // Label (HTML overlay)
      planetMeshes.push({ mesh, pivot, planet: p });
    });

    planetsRef.current = planetMeshes;

    // Resize handler
    const onResize = () => {
      if (!mountRef.current) return;
      const w = mountRef.current.clientWidth;
      const h = mountRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
      planetsRef.current   = [];
      orbitsRef.current    = [];
      trailsRef.current    = [];
      sceneRef.current     = null;
      rendererRef.current  = null;
      cameraRef.current    = null;
    };
  }, [threeLoaded]);

  // ── Camera helper ─────────────────────────────────────────────────────────
  function updateCameraPos(camera) {
    const THREE = window.THREE;
    if (!camera) return;
    camera.position.set(
      camRadius.current * Math.sin(camPhi.current) * Math.sin(camTheta.current),
      camRadius.current * Math.cos(camPhi.current),
      camRadius.current * Math.sin(camPhi.current) * Math.cos(camTheta.current)
    );
    camera.lookAt(new THREE.Vector3(0, 0, 0));
  }

  // ── Animation loop ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!threeLoaded) return;

    const animate = () => {
      animRef.current = requestAnimationFrame(animate);
      if (!sceneRef.current || !rendererRef.current || !cameraRef.current) return;

      clockRef.current += 0.003 * simSpeed;
      const t = clockRef.current;

      planetsRef.current.forEach(({ mesh, pivot, planet }, i) => {
        // Orbit
        const angle        = t * planet.speed * 0.03;
        pivot.rotation.y   = (i / PLANET_DATA.length) * Math.PI * 2 + angle;
        // Self-rotation
        mesh.rotation.y   += 0.005;

        // Trail update
        const trail = trailsRef.current[i];
        if (trail && showTrails) {
          const wp = new window.THREE.Vector3();
          mesh.getWorldPosition(wp);
          trail.positions.unshift([wp.x, wp.y, wp.z]);
          if (trail.positions.length > 120) trail.positions.pop();
          const posArr = trail.line.geometry.attributes.position.array;
          trail.positions.forEach((pos, j) => {
            posArr[j*3]   = pos[0];
            posArr[j*3+1] = pos[1];
            posArr[j*3+2] = pos[2];
          });
          trail.line.geometry.attributes.position.needsUpdate = true;
          trail.line.geometry.setDrawRange(0, trail.positions.length);
          trail.line.visible = showTrails;
        }
        if (trail) trail.line.visible = showTrails;
      });

      // Orbit rings visibility
      orbitsRef.current.forEach(r => r.visible = showOrbits);

      rendererRef.current.render(sceneRef.current, cameraRef.current);

      // HTML labels
      if (showLabels && cameraRef.current) {
        updateLabels();
      }
    };

    animate();
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [threeLoaded, simSpeed, showTrails, showOrbits, showLabels]);

  // ── Update HTML labels ────────────────────────────────────────────────────
  const updateLabels = () => {
    if (!cameraRef.current || !rendererRef.current || !mountRef.current) return;
    const THREE    = window.THREE;
    const camera   = cameraRef.current;
    const renderer = rendererRef.current;
    const W        = renderer.domElement.clientWidth;
    const H        = renderer.domElement.clientHeight;

    planetsRef.current.forEach(({ mesh, planet }, i) => {
      const wp  = new THREE.Vector3();
      mesh.getWorldPosition(wp);
      const projected = wp.clone().project(camera);
      const x   = (projected.x  *  0.5 + 0.5) * W;
      const y   = (projected.y * -0.5 + 0.5) * H;
      const el  = document.getElementById(`planet-label-${i}`);
      if (el) {
        if (projected.z < 1) {
          el.style.left      = `${x}px`;
          el.style.top       = `${y - planet.radius * 18}px`;
          el.style.display   = 'block';
          el.style.opacity   = showLabels ? '1' : '0';
        } else {
          el.style.display = 'none';
        }
      }
    });
  };

  // ── Mouse controls ────────────────────────────────────────────────────────
  const onMouseDown = useCallback((e) => {
    isDragging.current = true;
    lastMouse.current  = { x: e.clientX, y: e.clientY };
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!mountRef.current) return;
    const rect = mountRef.current.getBoundingClientRect();
    mouseRef.current = {
      x:  ((e.clientX - rect.left) / rect.width)  *  2 - 1,
      y: -((e.clientY - rect.top)  / rect.height) *  2 + 1,
    };

    if (!isDragging.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    camTheta.current -= dx * 0.005;
    camPhi.current    = Math.max(0.2, Math.min(Math.PI - 0.2, camPhi.current + dy * 0.005));
    lastMouse.current = { x: e.clientX, y: e.clientY };
    updateCameraPos(cameraRef.current);
  }, []);

  const onMouseUp = useCallback(() => { isDragging.current = false; }, []);

  const onWheel = useCallback((e) => {
    camRadius.current = Math.max(15, Math.min(200, camRadius.current + e.deltaY * 0.05));
    updateCameraPos(cameraRef.current);
  }, []);

  // ── Click → Raycasting ────────────────────────────────────────────────────
  const onClick = useCallback((e) => {
    if (!sceneRef.current || !cameraRef.current || !mountRef.current) return;
    const THREE  = window.THREE;
    const rect   = mountRef.current.getBoundingClientRect();
    const mouse  = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width)  *  2 - 1,
      -((e.clientY - rect.top) / rect.height) *  2 + 1
    );
    const ray    = new THREE.Raycaster();
    ray.setFromCamera(mouse, cameraRef.current);
    ray.params.Points = { threshold: 0.5 };

    const meshes = [
      ...planetsRef.current.map(p => p.mesh),
      ...sceneRef.current.children.filter(c => c.userData?.isSun),
    ];
    const hits = ray.intersectObjects(meshes, true);
    if (hits.length > 0) {
      let obj = hits[0].object;
      while (obj && !obj.userData?.name) obj = obj.parent;
      if (obj?.userData?.name) {
        if (obj.userData.isSun) {
          setSelected({ name:'Sun', type:'Star', distance:'0', period:'—',
            moons:0, mass:'1.99 × 10³⁰ kg', temp:'5,500°C surface',
            description:'Our star. Contains 99.86% of solar system mass.',
            color:'#ffd600', radius:'696,340 km', speed:'—',
            angle_deg: 0, x_au: 0, y_au: 0 });
        } else {
          const p  = obj.userData;
          const wp = new THREE.Vector3();
          obj.getWorldPosition(wp);
          const keplerPos = computeKeplerPosition(p.name);
          setSelected({
            ...p,
            color:     PLANET_COLORS_HEX[p.name] || '#ffffff',
            angle_deg: keplerPos.angle_deg,
            x_au:      keplerPos.x_au,
            y_au:      keplerPos.y_au,
          });
          // Zoom to planet
          const dist = wp.length();
          camRadius.current = dist + 15;
          camTheta.current  = Math.atan2(wp.x, wp.z);
          camPhi.current    = Math.PI / 2.5;
          updateCameraPos(cameraRef.current);
        }
      } else {
        setSelected(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ── Touch controls ────────────────────────────────────────────────────────
  const lastTouch  = useRef(null);
  const lastPinch  = useRef(null);
  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 1) lastTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinch.current = Math.sqrt(dx*dx + dy*dy);
    }
  }, []);
  const onTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1 && lastTouch.current) {
      const dx = e.touches[0].clientX - lastTouch.current.x;
      const dy = e.touches[0].clientY - lastTouch.current.y;
      camTheta.current -= dx * 0.006;
      camPhi.current    = Math.max(0.2, Math.min(Math.PI - 0.2, camPhi.current + dy * 0.006));
      lastTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      updateCameraPos(cameraRef.current);
    }
    if (e.touches.length === 2 && lastPinch.current) {
      const dx   = e.touches[0].clientX - e.touches[1].clientX;
      const dy   = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx*dx + dy*dy);
      camRadius.current = Math.max(15, Math.min(200, camRadius.current - (dist - lastPinch.current) * 0.3));
      lastPinch.current = dist;
      updateCameraPos(cameraRef.current);
    }
  }, []);

  const resetCamera = () => {
    camTheta.current  = 0.3;
    camPhi.current    = 1.1;
    camRadius.current = 90;
    updateCameraPos(cameraRef.current);
    setSelected(null);
  };

  // Get live angle from Three.js pivot rotation
  const getLiveAngle = (planetName) => {
    const found = planetsRef.current.find(p => p.planet.name === planetName);
    if (!found) return '—';
    const angle = ((found.pivot.rotation.y * 180 / Math.PI) % 360 + 360) % 360;
    return `${angle.toFixed(2)}°`;
  };

  return (
    <Box sx={{ position:'relative', width:'100%' }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, flexWrap:'wrap', gap:1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight:800 }}>
            🌌 3D Solar System
          </Typography>
          <Typography variant="body2" sx={{ color:'text.secondary', mt:0.5 }}>
            Interactive 3D visualization • Click any planet to explore
          </Typography>
        </Box>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'center' }}>
          <Chip label={`Speed: ${simSpeed}x`} size="small"
            sx={{ bgcolor:'rgba(255,255,255,0.1)', color:'primary.light', fontWeight:700 }} />
          <Tooltip title="Slower"><IconButton size="small"
            onClick={() => setSimSpeed(s => Math.max(0.1, s - 0.5))}
            sx={{ border:'1px solid rgba(255,255,255,0.3)', color:'primary.light' }}>
            <ZoomOut fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Faster"><IconButton size="small"
            onClick={() => setSimSpeed(s => Math.min(10, s + 0.5))}
            sx={{ border:'1px solid rgba(255,255,255,0.3)', color:'primary.light' }}>
            <ZoomIn fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Reset view"><IconButton size="small" onClick={resetCamera}
            sx={{ border:'1px solid rgba(255,255,255,0.3)', color:'primary.light' }}>
            <CenterFocusStrong fontSize="small" /></IconButton></Tooltip>

         <Tooltip title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}>
            <IconButton size="small"
              onClick={() => {
                const el = mountRef.current?.parentElement;
                if (!el) return;
                if (!document.fullscreenElement) {
                  el.requestFullscreen?.();
                } else {
                  document.exitFullscreen?.();
                }
              }}
              sx={{
                border: isFullscreen
                  ? '1px solid rgba(239,83,80,0.5)'
                  : '1px solid rgba(255,255,255,0.3)',
                color: isFullscreen ? '#ef5350' : 'primary.light',
                '&:hover':{ bgcolor: isFullscreen ? 'rgba(239,83,80,0.15)' : 'rgba(2,136,209,0.2)' },
              }}>
              {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Controls ────────────────────────────────────────────────────── */}
      <Box sx={{ display:'flex', gap:1.5, mb:1.5, flexWrap:'wrap', alignItems:'center' }}>
        <FormControlLabel control={<Switch checked={showOrbits} size="small" onChange={e=>setShowOrbits(e.target.checked)} />}
          label={<Typography variant="caption">Orbits</Typography>} />
        <FormControlLabel control={<Switch checked={showTrails} size="small" onChange={e=>setShowTrails(e.target.checked)} />}
          label={<Typography variant="caption">Trails</Typography>} />
        <FormControlLabel control={<Switch checked={showLabels} size="small" onChange={e=>setShowLabels(e.target.checked)} />}
          label={<Typography variant="caption">Labels</Typography>} />
        <Typography variant="caption" sx={{ color:'text.secondary', ml:'auto' }}>
          🖱 Drag to rotate • Scroll to zoom • Click planet for info
        </Typography>
      </Box>

      {/* ── 3D Canvas + Labels ───────────────────────────────────────────── */}
      <Box sx={{ position:'relative', borderRadius:3, overflow:'hidden',
        border:'1px solid rgba(255,255,255,0.2)', bgcolor:'#020408' }}>

        {/* Three.js mount */}
        <Box
          ref={mountRef}
          sx={{ width:'100%', height:580, cursor: isDragging.current ? 'grabbing' : 'grab' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onWheel={onWheel}
          onClick={onClick}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={() => { lastTouch.current = null; lastPinch.current = null; }}
        />

        {/* HTML planet labels */}
        {PLANET_DATA.map((p, i) => (
          <Box
            key={p.name}
            id={`planet-label-${i}`}
            sx={{
              position:'absolute', display:'none', pointerEvents:'none',
              transform:'translateX(-50%)',
              transition:'opacity 0.2s',
            }}
          >
            <Typography variant="caption" sx={{
              color: PLANET_COLORS_HEX[p.name],
              fontWeight:700, fontSize:10,
              textShadow:'0 0 8px rgba(0,0,0,0.9)',
              bgcolor:'rgba(2,4,8,0.5)',
              px:0.6, py:0.2, borderRadius:0.5,
              whiteSpace:'nowrap',
            }}>
              {p.name}
            </Typography>
          </Box>
        ))}

        {/* Fullscreen exit button — only shows in fullscreen */}
        {isFullscreen && (
          <Box sx={{
            position: 'absolute', top: 16, right: 16, zIndex: 9999,
          }}>
            <Tooltip title="Exit Full Screen">
              <IconButton
                onClick={() => document.exitFullscreen?.()}
                sx={{
                  bgcolor: 'rgba(239,83,80,0.2)',
                  color: '#ef5350',
                  border: '1px solid rgba(239,83,80,0.5)',
                  '&:hover': { bgcolor: 'rgba(239,83,80,0.4)' },
                }}>
                <FullscreenExit />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Loading overlay */}
        {!threeLoaded && (
          <Box sx={{ position:'absolute', inset:0, display:'flex',
            alignItems:'center', justifyContent:'center', bgcolor:'rgba(2,4,8,0.95)',
            flexDirection:'column', gap:2 }}>
            <Typography sx={{ fontSize:48 }}>🌌</Typography>
            <Typography variant="body1" sx={{ color:'primary.light', fontWeight:700 }}>
              Loading 3D Engine...
            </Typography>
          </Box>
        )}

        {/* Click hint */}
        {threeLoaded && !selected && (
          <Box sx={{ position:'absolute', bottom:16, left:'50%', transform:'translateX(-50%)',
            bgcolor:'rgba(5,13,26,0.7)', border:'1px solid rgba(255,255,255,0.2)',
            borderRadius:2, px:2, py:0.8, pointerEvents:'none' }}>
            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.7)' }}>
              Click any planet or the Sun for details
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Planet quick-select ──────────────────────────────────────────── */}
      <Box sx={{ display:'flex', gap:0.8, mt:1.5, flexWrap:'wrap' }}>
        <Chip label="☀ Sun" size="small" onClick={() => {
          setSelected({ name:'Sun', type:'Star', distance:'0', period:'—',
            moons:0, mass:'1.99 × 10³⁰ kg', temp:'5,500°C surface',
            description:'Our star. Contains 99.86% of solar system mass.',
            color:'#ffd600', radius:'696,340 km', speed:'—' });
          camRadius.current = 20; camPhi.current = 1.1; camTheta.current = 0.3;
          updateCameraPos(cameraRef.current);
        }} sx={{ bgcolor:'rgba(255,214,0,0.15)', color:'#ffd600', border:'1px solid rgba(255,214,0,0.3)', fontWeight:700, cursor:'pointer' }} />
        {PLANET_DATA.map(p => (
          <Chip key={p.name} label={p.name} size="small"
            onClick={() => {
              const found = planetsRef.current.find(pl => pl.planet.name === p.name);
              if (found) {
                const THREE     = window.THREE;
                const wp        = new THREE.Vector3();
                found.mesh.getWorldPosition(wp);
                const keplerPos = computeKeplerPosition(p.name);
                setSelected({
                  ...p,
                  color:     PLANET_COLORS_HEX[p.name],
                  angle_deg: keplerPos.angle_deg,
                  x_au:      keplerPos.x_au,
                  y_au:      keplerPos.y_au,
                });
                camRadius.current = wp.length() + 15;
                camTheta.current  = Math.atan2(wp.x, wp.z);
                camPhi.current    = Math.PI / 2.5;
                updateCameraPos(cameraRef.current);
              }
            }}
            sx={{
              bgcolor:`${PLANET_COLORS_HEX[p.name]}18`,
              color: PLANET_COLORS_HEX[p.name],
              border:`1px solid ${PLANET_COLORS_HEX[p.name]}44`,
              fontWeight:700, cursor:'pointer', fontSize:11,
              '&:hover':{ bgcolor:`${PLANET_COLORS_HEX[p.name]}30` },
            }} />
        ))}
      </Box>

      {/* ── Info Panel ───────────────────────────────────────────────────── */}
      {selected && (
        <Card sx={{
          mt:2, border:`1px solid ${selected.color}44`,
          background:`linear-gradient(135deg, ${selected.color}08, rgba(5,13,26,0.98))`,
          position:'relative', overflow:'hidden',
          '&::before':{ content:'""', position:'absolute', top:0, left:0, right:0, height:3, bgcolor: selected.color },
        }}>
          <CardContent sx={{ p:2.5 }}>
            <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', mb:2 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                <Typography sx={{ fontSize:40 }}>
                  {selected.name === 'Sun' ? '☀️' :
                   selected.name === 'Earth' ? '🌍' :
                   selected.name === 'Saturn' ? '🪐' :
                   selected.name === 'Jupiter' ? '🪐' : '🌑'}
                </Typography>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight:900, color: selected.color }}>
                    {selected.name}
                  </Typography>
                  <Chip label={selected.type || 'Planet'} size="small"
                    sx={{ bgcolor:`${selected.color}22`, color: selected.color,
                      border:`1px solid ${selected.color}44`, fontWeight:700, fontSize:10 }} />
                </Box>
              </Box>
              <IconButton onClick={() => setSelected(null)} size="small"
                sx={{ color:'text.secondary' }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {selected.description && (
              <Typography variant="body2" sx={{ color:'text.secondary', lineHeight:1.8, mb:2 }}>
                {selected.description}
              </Typography>
            )}

            <Divider sx={{ borderColor:`${selected.color}18`, mb:2 }} />

            <Grid container spacing={1.5}>
                  {[
                { label:'Distance from Sun', value: selected.distance,                                    icon:'📍' },
                { label:'Orbital Period',    value: selected.period,                                      icon:'🔄' },
                { label:'Moons',             value: selected.moons,                                       icon:'🌙' },
                { label:'Mass',              value: selected.mass,                                        icon:'⚖️'  },
                { label:'Temperature',       value: selected.temp,                                        icon:'🌡️'  },
                { label:'Orbital Speed',     value: selected.speedKms ? `${selected.speedKms} km/s` : (selected.speed !== '—' ? selected.speed : '—'), icon:'⚡' },
                { label:'Current Angle',     value: selected.angle_deg != null ? `${Number(selected.angle_deg).toFixed(2)}°` : getLiveAngle(selected.name), icon:'🧭' },
                { label:'X Position (AU)',   value: selected.x_au    != null ? `${Number(selected.x_au).toFixed(4)} AU`    : '—', icon:'↔️' },
                { label:'Y Position (AU)',   value: selected.y_au    != null ? `${Number(selected.y_au).toFixed(4)} AU`    : '—', icon:'↕️' },
                { label:'Last Updated',      value: new Date().toLocaleTimeString(),                      icon:'🕐' },
              ].map(({ label, value, icon }) => value != null && value !== undefined && (
                <Grid item xs={6} sm={4} key={label}>
                  <Box sx={{ p:1.5, borderRadius:2, textAlign:'center',
                    bgcolor:`${selected.color}06`,
                    border:`1px solid ${selected.color}18`,
                    height:'100%',
                  }}>
                    <Typography sx={{ fontSize:18, mb:0.3 }}>{icon}</Typography>
                    <Typography variant="body2" sx={{ fontWeight:800, color: selected.color, fontSize:11 }}>
                      {String(value)}
                    </Typography>
                    <Typography variant="caption" sx={{ color:'text.secondary', fontSize:9 }}>
                      {label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
